import type { JSX } from 'react';
import { useState, useRef, useEffect } from 'react';

import { TextHighlight } from '../../text-highlight';

interface SubtitleComponentProps {
  subtitle: string;
  collapsible?: boolean;
  maxLines?: number;
  searchTerm?: string;
}

export function SubtitleComponent({
  subtitle,
  collapsible = false,
  maxLines = 2,
  searchTerm,
}: SubtitleComponentProps): JSX.Element {
  // State for controlling text expansion (only used when collapsible)
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Check if content overflows and needs truncation
  useEffect(() => {
    if (collapsible && contentRef.current) {
      const element = contentRef.current;

      // Create a temporary element to measure full height
      const tempElement = element.cloneNode(true) as HTMLElement;
      tempElement.style.position = 'absolute';
      tempElement.style.visibility = 'hidden';
      tempElement.style.height = 'auto';
      tempElement.style.maxHeight = 'none';
      tempElement.className = tempElement.className.replace(/line-clamp-\d+/, '');

      document.body.appendChild(tempElement);
      const fullHeight = tempElement.offsetHeight;
      document.body.removeChild(tempElement);

      const clampedHeight = element.offsetHeight;

      // If full height is greater than clamped height, we have overflow
      setHasOverflow(fullHeight > clampedHeight);
    }
  }, [subtitle, maxLines, collapsible]);

  if (collapsible) {
    // Collapsible mode: CSS line-clamp with expand/collapse functionality
    const lineClampClass = isTextExpanded ? '' : `line-clamp-${maxLines}`;

    return (
      <div className="flex flex-col">
        <div ref={contentRef} className={`${lineClampClass} overflow-hidden`} style={{ whiteSpace: 'pre-line' }}>
          {subtitle.split('\n').map((line, index) => (
            <div key={`${line.slice(0, 20)}-${line.length}`} className={index === 0 ? 'font-medium' : undefined}>
              {searchTerm ? <TextHighlight text={line} searchTerm={searchTerm} /> : line}
            </div>
          ))}
        </div>
        {hasOverflow && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsTextExpanded(!isTextExpanded);
            }}
            className="text-brand-400 hover:text-brand-500 mt-1 inline-flex items-center gap-1 text-left text-sm font-medium"
            type="button"
          >
            {isTextExpanded ? (
              <>
                Show less
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </>
            ) : (
              <>
                Show more
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </>
            )}
          </button>
        )}
      </div>
    );
  }

  // Non-collapsible mode: show full text with original formatting
  return (
    <div className="flex flex-col">
      {subtitle.split('\n').map((line, index) => (
        <div key={`${line.slice(0, 20)}-${line.length}`} className={index === 0 ? 'font-medium' : undefined}>
          {searchTerm ? <TextHighlight text={line} searchTerm={searchTerm} /> : line}
        </div>
      ))}
    </div>
  );
}
